import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { supabase } from '@/lib/supabase'
import AdminStatsCards from './components/StatsCards'
import NewsManager from './components/NewsManager'
import UserManager from './components/UserManager'
import HighlightsManager from './components/HighlightsManager'
import AnalyticsDashboard from './components/AnalyticsDashboard'
import ClipManager from './components/ClipManager'
import SourceManager from './components/SourceManager'
import CronManager from './components/CronManager'
import GerenciarLive from './components/GerenciarLive'
import MercadoDaBolaManager from './components/MercadoDaBolaManager'
import { BRAND_LOGO_URL, BRAND_NAME } from '@/constants/brand'
import BrandSettingsManager from './components/BrandSettingsManager'
import { useBrandSettings } from '@/hooks/useBrandSettings'

const tabs = [
  { id: 'dashboard', label: 'Dashboard', icon: 'ri-dashboard-line' },
  { id: 'analytics', label: 'Analytics', icon: 'ri-bar-chart-line' },
  { id: 'news', label: 'Notícias', icon: 'ri-article-line' },
  { id: 'mercado', label: 'Mercado da Bola', icon: 'ri-exchange-line' },
  { id: 'clipagem', label: 'Clipagem', icon: 'ri-rss-line', showBadge: true },
  { id: 'sources', label: 'Fontes RSS', icon: 'ri-database-2-line' },
  { id: 'automation', label: 'Automação', icon: 'ri-time-line' },
  { id: 'users', label: 'Usuários', icon: 'ri-user-line' },
  { id: 'highlights', label: 'Destaques', icon: 'ri-star-line' },
  { id: 'aovivo', label: 'Ao Vivo', icon: 'ri-live-line' },
  { id: 'settings', label: 'Aparência', icon: 'ri-palette-line' },
]

function AdminLogin() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const { logoSize } = useBrandSettings()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      })
      if (signInError) {
        if (signInError.message.includes('Invalid login credentials')) {
          setError('Email ou senha incorretos.')
        } else {
          setError('Erro ao fazer login. Tente novamente.')
        }
        setLoading(false)
      }
    } catch {
      setError('Erro de conexão. Verifique sua internet.')
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-alvinegro-light flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl p-8 max-w-sm w-full shadow-sm border border-alvinegro-border">
        <div className="text-center mb-8">
          <img
            src={BRAND_LOGO_URL}
            alt={BRAND_NAME}
            className="object-contain mx-auto mb-4"
            style={{ width: Math.max(80, logoSize), height: Math.max(80, logoSize) }}
          />
          <h2 className="font-serif font-bold text-xl text-alvinegro-black mb-1">Painel Administrativo</h2>
          <p className="text-sm text-alvinegro-muted">Central Alvinegra</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-alvinegro-dark mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg border border-alvinegro-border text-sm focus:outline-none focus:border-alvinegro-black"
              placeholder="seu@email.com"
              autoComplete="email"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-alvinegro-dark mb-1">Senha</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg border border-alvinegro-border text-sm focus:outline-none focus:border-alvinegro-black"
              placeholder="Digite sua senha"
              autoComplete="current-password"
              required
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
              <i className="ri-error-warning-line text-base shrink-0"></i>
              <span>{error}</span>
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-alvinegro-black text-white py-2.5 rounded-full text-sm font-medium hover:bg-alvinegro-dark transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-loader-4-line animate-spin"></i>
                Entrando...
              </span>
            ) : (
              'Entrar'
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={() => window.location.href = '/'}
            className="text-sm text-alvinegro-muted hover:text-alvinegro-dark transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line mr-1"></i>
            Voltar para o site
          </button>
        </div>
      </div>
    </div>
  )
}

export default function AdminPage() {
  const navigate = useNavigate()
  const { user, profile, isLoading, isAdmin, signOut } = useAuth()
  const [activeTab, setActiveTab] = useState('dashboard')
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [pendingClips, setPendingClips] = useState(0)
  const { logoSize } = useBrandSettings()
  const adminLogoSize = Math.max(40, Math.round(logoSize * 0.7))

  useEffect(() => {
    const handleTabChange = (e: Event) => {
      const customEvent = e as CustomEvent
      if (customEvent.detail) {
        setActiveTab(customEvent.detail)
      }
    }
    window.addEventListener('admin-tab-change', handleTabChange)
    return () => window.removeEventListener('admin-tab-change', handleTabChange)
  }, [])

  useEffect(() => {
    async function fetchPending() {
      const { count } = await supabase
        .from('rss_feeds')
        .select('id', { count: 'exact', head: true })
        .eq('status', 'draft')
      setPendingClips(count || 0)
    }
    fetchPending()

    const channel = supabase
      .channel('rss_feeds_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'rss_feeds' }, () => {
        fetchPending()
      })
      .subscribe()

    return () => {
      channel.unsubscribe()
    }
  }, [])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-alvinegro-light flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-alvinegro-black border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (!user) {
    return <AdminLogin />
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-alvinegro-light flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl p-8 max-w-sm w-full shadow-sm border border-alvinegro-border text-center">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <i className="ri-shield-user-line text-2xl text-red-600"></i>
          </div>
          <h2 className="font-serif font-bold text-xl text-alvinegro-black mb-2">Acesso Negado</h2>
          <p className="text-sm text-alvinegro-muted mb-6 leading-relaxed">
            Sua conta ({user.email}) não possui permissão de administrador.
            Entre em contato com o administrador do site para solicitar acesso.
          </p>
          <div className="space-y-2">
            <button
              onClick={() => signOut()}
              className="w-full bg-alvinegro-black text-white py-2.5 rounded-full text-sm font-medium hover:bg-alvinegro-dark transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-logout-box-line mr-2"></i>
              Sair desta conta
            </button>
            <button
              onClick={() => window.location.href = '/'}
              className="w-full text-sm text-alvinegro-muted hover:text-alvinegro-dark transition-colors cursor-pointer py-2"
            >
              <i className="ri-arrow-left-line mr-1"></i>
              Voltar para o site
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-alvinegro-light flex">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex flex-col w-64 bg-white border-r border-alvinegro-border fixed h-full">
        <div className="p-6 border-b border-alvinegro-border">
          <div className="flex items-center gap-2">
            <img
              src={BRAND_LOGO_URL}
              alt={BRAND_NAME}
              className="object-contain"
              style={{ width: adminLogoSize, height: adminLogoSize }}
            />
            <div>
              <h2 className="font-serif font-bold text-sm text-alvinegro-black">Admin</h2>
              <p className="text-xs text-alvinegro-muted">Central Alvinegra</p>
            </div>
          </div>
        </div>
        <nav className="flex-1 py-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full flex items-center gap-3 px-6 py-3 text-sm transition-colors cursor-pointer relative ${
                activeTab === tab.id
                  ? 'bg-alvinegro-black text-white'
                  : 'text-alvinegro-dark hover:bg-alvinegro-light'
              }`}
            >
              <i className={`${tab.icon} text-base`}></i>
              <span className="flex-1 text-left">{tab.label}</span>
              {tab.showBadge && pendingClips > 0 && (
                <span className="ml-auto w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center shrink-0">
                  {pendingClips > 99 ? '99+' : pendingClips}
                </span>
              )}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-alvinegro-border">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-8 h-8 rounded-full bg-alvinegro-black flex items-center justify-center overflow-hidden">
              {profile?.avatar ? (
                <img src={profile.avatar} alt="" className="w-full h-full object-cover" />
              ) : (
                <i className="ri-user-line text-white text-sm"></i>
              )}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-alvinegro-black truncate">{profile?.name || 'Admin'}</p>
              <p className="text-xs text-alvinegro-muted">{profile?.role || 'admin'}</p>
            </div>
          </div>
          <div className="mt-2 space-y-1">
            <button
              onClick={() => navigate('/')}
              className="w-full flex items-center gap-2 px-2 py-2 text-xs text-alvinegro-muted hover:text-alvinegro-dark transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Voltar ao site
            </button>
            <button
              onClick={() => signOut()}
              className="w-full flex items-center gap-2 px-2 py-2 text-xs text-red-600 hover:text-red-600 transition-colors cursor-pointer"
            >
              <i className="ri-logout-box-line"></i>
              Sair
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Sidebar Toggle */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="w-10 h-10 rounded-full bg-white shadow-lg border border-alvinegro-border flex items-center justify-center cursor-pointer"
        >
          <i className={`ri-${sidebarOpen ? 'close' : 'menu'}-line text-lg text-alvinegro-dark`}></i>
        </button>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <>
          <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setSidebarOpen(false)} />
          <aside className="lg:hidden fixed top-0 left-0 w-64 h-full bg-white border-r border-alvinegro-border z-50 flex flex-col">
            <div className="p-6 border-b border-alvinegro-border">
              <div className="flex items-center gap-2">
                <img
                  src={BRAND_LOGO_URL}
                  alt={BRAND_NAME}
                  className="object-contain"
                  style={{ width: adminLogoSize, height: adminLogoSize }}
                />
                <div>
                  <h2 className="font-serif font-bold text-sm text-alvinegro-black">Admin</h2>
                  <p className="text-xs text-alvinegro-muted">Central Alvinegra</p>
                </div>
              </div>
            </div>
            <nav className="flex-1 py-4">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => { setActiveTab(tab.id); setSidebarOpen(false) }}
                  className={`w-full flex items-center gap-3 px-6 py-3 text-sm transition-colors cursor-pointer relative ${
                    activeTab === tab.id
                      ? 'bg-alvinegro-black text-white'
                      : 'text-alvinegro-dark hover:bg-alvinegro-light'
                  }`}
                >
                  <i className={`${tab.icon} text-base`}></i>
                  <span className="flex-1 text-left">{tab.label}</span>
                  {tab.showBadge && pendingClips > 0 && (
                    <span className="ml-auto w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center shrink-0">
                      {pendingClips > 99 ? '99+' : pendingClips}
                    </span>
                  )}
                </button>
              ))}
            </nav>
            <div className="p-4 border-t border-alvinegro-border">
              <button
                onClick={() => navigate('/')}
                className="w-full flex items-center gap-2 px-2 py-2 text-sm text-alvinegro-dark hover:bg-alvinegro-light rounded-lg transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                Voltar ao site
              </button>
            </div>
          </aside>
        </>
      )}

      {/* Main Content */}
      <main className="flex-1 lg:ml-64">
        {/* Header */}
        <div className="bg-white border-b border-alvinegro-border px-4 md:px-8 py-4 md:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="font-serif font-bold text-xl md:text-2xl text-alvinegro-black">
                {tabs.find((t) => t.id === activeTab)?.label}
              </h1>
              <p className="text-sm text-alvinegro-muted mt-1">
                {activeTab === 'dashboard' && 'Visão geral do portal'}
                {activeTab === 'analytics' && 'Analytics de audiência e acesso'}
                {activeTab === 'news' && 'Gerencie todas as notícias do portal'}
                {activeTab === 'mercado' && 'Gerencie rumores, sondagens, negociações e transferências'}
                {activeTab === 'clipagem' && 'Central de clipagem e notícias importadas'}
                {activeTab === 'sources' && 'Gerencie fontes RSS de notícias'}
                {activeTab === 'automation' && 'Gerencie automações e cron jobs'}
                {activeTab === 'users' && 'Gerencie usuários e permissões'}
                {activeTab === 'highlights' && 'Configure os destaques da home'}
                {activeTab === 'aovivo' && 'Gerencie o link da transmissão ao vivo'}
                {activeTab === 'settings' && 'Ajuste a identidade visual do site'}
              </p>
            </div>
            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-alvinegro-black text-white text-xs font-medium">
              <i className="ri-shield-check-line text-xs"></i>
              Administrador
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 md:p-8">
          {activeTab === 'dashboard' && (
            <div className="space-y-6">
              <AdminStatsCards />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl border border-alvinegro-border p-6">
                  <h3 className="font-semibold text-alvinegro-black mb-4">Acesso rápido</h3>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setActiveTab('news')}
                      className="flex items-center gap-3 p-4 rounded-xl border border-alvinegro-border hover:bg-alvinegro-light transition-colors text-left cursor-pointer"
                    >
                      <div className="w-10 h-10 rounded-full bg-alvinegro-black flex items-center justify-center">
                        <i className="ri-article-line text-white"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-alvinegro-black">Notícias</p>
                        <p className="text-xs text-alvinegro-muted">Criar e editar</p>
                      </div>
                    </button>
                    <button
                      onClick={() => setActiveTab('mercado')}
                      className="flex items-center gap-3 p-4 rounded-xl border border-alvinegro-border hover:bg-alvinegro-light transition-colors text-left cursor-pointer"
                    >
                      <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center">
                        <i className="ri-exchange-line text-white"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-alvinegro-black">Mercado</p>
                        <p className="text-xs text-alvinegro-muted">Transferências</p>
                      </div>
                    </button>
                    <button
                      onClick={() => setActiveTab('clipagem')}
                      className="flex items-center gap-3 p-4 rounded-xl border border-alvinegro-border hover:bg-alvinegro-light transition-colors text-left cursor-pointer relative"
                    >
                      <div className="w-10 h-10 rounded-full bg-alvinegro-dark flex items-center justify-center">
                        <i className="ri-rss-line text-white"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-alvinegro-black">Clipagem</p>
                        <p className="text-xs text-alvinegro-muted">Notícias importadas</p>
                      </div>
                      {pendingClips > 0 && (
                        <span className="absolute top-3 right-3 w-6 h-6 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                          {pendingClips > 9 ? '9+' : pendingClips}
                        </span>
                      )}
                    </button>
                    <button
                      onClick={() => setActiveTab('users')}
                      className="flex items-center gap-3 p-4 rounded-xl border border-alvinegro-border hover:bg-alvinegro-light transition-colors text-left cursor-pointer"
                    >
                      <div className="w-10 h-10 rounded-full bg-alvinegro-dark flex items-center justify-center">
                        <i className="ri-user-line text-white"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-alvinegro-black">Usuários</p>
                        <p className="text-xs text-alvinegro-muted">Gerenciar acesso</p>
                      </div>
                    </button>
                    <button
                      onClick={() => setActiveTab('highlights')}
                      className="flex items-center gap-3 p-4 rounded-xl border border-alvinegro-border hover:bg-alvinegro-light transition-colors text-left cursor-pointer"
                    >
                      <div className="w-10 h-10 rounded-full bg-yellow-500 flex items-center justify-center">
                        <i className="ri-star-line text-white"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-alvinegro-black">Destaques</p>
                        <p className="text-xs text-alvinegro-muted">Configurar home</p>
                      </div>
                    </button>
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-alvinegro-border p-6">
                  <h3 className="font-semibold text-alvinegro-black mb-4">Links úteis</h3>
                  <div className="space-y-2">
                    <button
                      onClick={() => navigate('/noticias')}
                      className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-alvinegro-light transition-colors text-left cursor-pointer"
                    >
                      <span className="text-sm text-alvinegro-dark">Ver site - Notícias</span>
                      <i className="ri-arrow-right-line text-alvinegro-muted"></i>
                    </button>
                    <button
                      onClick={() => navigate('/mercado-da-bola')}
                      className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-alvinegro-light transition-colors text-left cursor-pointer"
                    >
                      <span className="text-sm text-alvinegro-dark">Ver Mercado da Bola</span>
                      <i className="ri-arrow-right-line text-alvinegro-muted"></i>
                    </button>
                    <button
                      onClick={() => navigate('/moderacao')}
                      className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-alvinegro-light transition-colors text-left cursor-pointer"
                    >
                      <span className="text-sm text-alvinegro-dark">Painel de Moderação</span>
                      <i className="ri-arrow-right-line text-alvinegro-muted"></i>
                    </button>
                    <button
                      onClick={() => setActiveTab('sources')}
                      className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-alvinegro-light transition-colors text-left cursor-pointer"
                    >
                      <span className="text-sm text-alvinegro-dark">Gerenciar Fontes RSS</span>
                      <i className="ri-arrow-right-line text-alvinegro-muted"></i>
                    </button>
                    <button
                      onClick={() => navigate('/jogos')}
                      className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-alvinegro-light transition-colors text-left cursor-pointer"
                    >
                      <span className="text-sm text-alvinegro-dark">Página de Jogos</span>
                      <i className="ri-arrow-right-line text-alvinegro-muted"></i>
                    </button>
                    <button
                      onClick={() => setActiveTab('aovivo')}
                      className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-alvinegro-light transition-colors text-left cursor-pointer"
                    >
                      <span className="text-sm text-alvinegro-dark">Gerenciar Ao Vivo</span>
                      <i className="ri-arrow-right-line text-alvinegro-muted"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          {activeTab === 'analytics' && <AnalyticsDashboard />}
          {activeTab === 'news' && <NewsManager />}
          {activeTab === 'mercado' && <MercadoDaBolaManager />}
          {activeTab === 'clipagem' && <ClipManager />}
          {activeTab === 'sources' && <SourceManager />}
          {activeTab === 'automation' && <CronManager />}
          {activeTab === 'users' && <UserManager />}
          {activeTab === 'highlights' && <HighlightsManager />}
          {activeTab === 'aovivo' && <GerenciarLive />}
          {activeTab === 'settings' && <BrandSettingsManager />}
        </div>
      </main>
    </div>
  )
}
